/**
 * **« Joue sur un écran plus large »** — SPEC-UI §2, premier point :
 *
 * > *Résolution de référence : desktop ≥ 1280 px ; layout fluide jusqu'à 960 px ; en dessous,
 * > bandeau « joue sur un écran plus large » ✎ (le mobile n'est pas dans la V1 ✎).*
 *
 * ## Pourquoi un bandeau et pas un mur
 *
 * Sous 960 px, ce jeu est **jouable mais mauvais** : la chambre, les sept jauges, les trois
 * tendances et la barre d'actions sont dessinées pour tenir ensemble à l'écran ⟦A-7⟧, et sur un
 * téléphone elles se mettent en colonne — chaque décision demande alors de faire défiler pour
 * retrouver le chiffre qui la motive. Les mini-jeux d'Atelier, eux, se jouent au clavier et à la
 * milliseconde.
 *
 * 🔑 **Mais « mauvais » n'est pas « interdit », et c'est toute la différence.** Un écran de
 * blocage prendrait à la place du joueur une décision qui lui appartient — et il enfermerait
 * quelqu'un qui a une partie en cours et voulait juste la relire dans le train. On prévient une
 * fois, on dit ce qui va manquer, et on rend la main.
 *
 * ## Il se referme, et il revient
 *
 * Le bandeau se ferme d'un clic ou d'`Échap`, et **ne réapparaît pas** tant que la fenêtre reste
 * étroite : la même phrase répétée à chaque rendu deviendrait du bruit. Il **revient** en
 * revanche si la fenêtre repasse au-dessus du seuil puis redescend — c'est un nouveau geste, donc
 * une nouvelle information. Rien n'est persisté : au prochain chargement, la V1 se re-présente.
 *
 * ⚠️ Aucun `window.alert` (CLAUDE.md) — c'est un bandeau dans le flux, pas une modale : il ne
 * vole pas le focus et ne bloque aucune action.
 */

import { useEffect, useState } from "react";

/**
 * Le seuil de §2. `max-width: 959.98px` plutôt que `959px` : entre les deux, une fenêtre de
 * 959,5 px (facteurs de zoom, écrans à densité fractionnaire) ne serait ni large ni étroite.
 */
export const REQUETE_PETIT_ECRAN = "(max-width: 959.98px)";

export function BandeauPetitEcran() {
  const [etroit, setEtroit] = useState(false);
  const [ferme, setFerme] = useState(false);

  useEffect(() => {
    if (typeof matchMedia !== "function") return;
    const mq = matchMedia(REQUETE_PETIT_ECRAN);
    const maj = () => {
      setEtroit(mq.matches);
      // Repasser en grand ARME de nouveau le bandeau : la prochaine fois qu'on rétrécit, la
      // phrase se redit. Ce n'est pas la même situation qu'un joueur qui n'a jamais bougé.
      if (!mq.matches) setFerme(false);
    };
    maj();
    mq.addEventListener("change", maj);
    // ⚠️ **Et `resize` aussi, ce qui n'est pas une ceinture de plus.** Vérifié sur cette base :
    // quand la fenêtre est redimensionnée par un pilotage externe (le viewport override des
    // outils de développement, celui qu'utilise l'automatisation), `matchMedia` répond
    // correctement à la lecture mais **n'émet aucun `change`**. Un composant qui ne s'abonne
    // qu'au `change` reste alors bloqué dans l'état du premier rendu — silencieusement, et
    // précisément quand on essaie de le vérifier. Les deux écouteurs appellent la même
    // fonction, qui relit `mq.matches` : aucun risque de désaccord entre eux.
    window.addEventListener("resize", maj);
    return () => {
      mq.removeEventListener("change", maj);
      window.removeEventListener("resize", maj);
    };
  }, []);

  useEffect(() => {
    if (!etroit || ferme) return;
    const auClavier = (e: KeyboardEvent) => {
      if (e.key === "Escape") setFerme(true);
    };
    window.addEventListener("keydown", auClavier);
    return () => window.removeEventListener("keydown", auClavier);
  }, [etroit, ferme]);

  if (!etroit || ferme) return null;

  return (
    <div className="bandeau-petit-ecran" role="status">
      <span aria-hidden="true">🖥</span>
      <p>
        <strong>Ça se joue sur un écran plus large.</strong> En dessous de 960 px, les jauges,
        les tendances et la barre d'actions passent en colonne — et les mini-jeux se jouent au
        clavier. Tu peux continuer ici, mais tu joues avec une main dans le dos.
      </p>
      <button type="button" className="bouton discret" onClick={() => setFerme(true)}>
        J'ai compris
      </button>
    </div>
  );
}
